import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiPhone, FiMail, FiMapPin, FiInstagram, FiFacebook, FiTwitter, FiDownload } = FiIcons;

const Footer = () => {
  const contactInfo = [
    { icon: FiPhone, label: 'Phone', value: '+94 77 123 4567' },
    { icon: FiMail, label: 'Email', value: 'info@jansensbungalow.com' },
    { icon: FiMapPin, label: 'Address', value: 'Kudawe, Sinharaja Forest Reserve' }
  ];

  const quickLinks = [
    'Birding Packages',
    'Accommodation',
    'Tour Guides',
    'Transportation',
    'Gallery',
    'Blog'
  ];

  const birdingResources = [
    'Endemic Species Checklist',
    'Best Time to Visit',
    'What to Bring',
    'Photography Tips',
    'Conservation Info',
    'eBird Hotspot'
  ];

  return (
    <footer className="bg-gradient-to-br from-slate-900 to-emerald-900 pt-20 pb-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
          {/* Brand & Description */}
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="lg:col-span-1"
          >
            <div className="flex items-center space-x-2 mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-full flex items-center justify-center">
                <span className="text-white font-bold text-lg">J</span>
              </div>
              <div>
                <h3 className="text-white font-bold text-xl font-playfair">Jansen's Bungalow</h3>
                <p className="text-emerald-300 text-xs">Sinharaja Birding Paradise</p>
              </div>
            </div>
            <p className="text-gray-300 mb-6 leading-relaxed">
              The premier luxury birding retreat at the Kudawe-Ullinduwawa border. 
              Experience Sri Lanka's endemic species in unmatched comfort.
            </p>
            <div className="flex space-x-4">
              {[FiFacebook, FiInstagram, FiTwitter].map((Icon, index) => (
                <motion.a
                  key={index}
                  href="#"
                  whileHover={{ scale: 1.1, y: -2 }}
                  className="bg-white/10 p-3 rounded-full text-white hover:bg-emerald-500 transition-colors"
                >
                  <SafeIcon icon={Icon} className="text-lg" />
                </motion.a>
              ))}
            </div>
          </motion.div>

          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1 }}
          >
            <h4 className="text-white font-semibold text-lg mb-6">Contact</h4>
            <div className="space-y-4">
              {contactInfo.map((contact, index) => (
                <div key={index} className="flex items-center space-x-3">
                  <SafeIcon icon={contact.icon} className="text-emerald-400 text-lg" />
                  <div>
                    <p className="text-gray-400 text-sm">{contact.label}</p>
                    <p className="text-white">{contact.value}</p>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Quick Links */}
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <h4 className="text-white font-semibold text-lg mb-6">Quick Links</h4>
            <div className="space-y-3">
              {quickLinks.map((link, index) => (
                <motion.a
                  key={index}
                  href="#"
                  whileHover={{ x: 5 }}
                  className="block text-gray-300 hover:text-emerald-300 transition-colors"
                >
                  {link}
                </motion.a>
              ))}
            </div>
          </motion.div>

          {/* Birding Resources */}
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
          >
            <h4 className="text-white font-semibold text-lg mb-6">Birding Resources</h4>
            <div className="space-y-3">
              {birdingResources.map((resource, index) => (
                <motion.a
                  key={index}
                  href="#"
                  whileHover={{ x: 5 }}
                  className="block text-gray-300 hover:text-emerald-300 transition-colors"
                >
                  {resource}
                </motion.a>
              ))}
            </div>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="mt-6 bg-gradient-to-r from-emerald-500 to-teal-500 text-white px-6 py-3 rounded-full font-medium flex items-center space-x-2 hover-lift"
            >
              <SafeIcon icon={FiDownload} className="text-lg" />
              <span>Field Guide PDF</span>
            </motion.button>
          </motion.div>
        </div>

        {/* Newsletter Signup */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="glass rounded-2xl p-8 mb-12"
        >
          <div className="text-center md:text-left md:flex md:items-center md:justify-between">
            <div className="mb-6 md:mb-0">
              <h4 className="text-white font-semibold text-xl mb-2">Stay Updated</h4>
              <p className="text-gray-300">Get weekly sighting reports and exclusive birding tips</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 md:max-w-md">
              <input
                type="email"
                placeholder="Enter your email"
                className="flex-1 bg-white/10 border border-white/20 rounded-full px-6 py-3 text-white placeholder-gray-400 focus:outline-none focus:border-emerald-400"
              />
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white px-8 py-3 rounded-full font-medium hover-lift whitespace-nowrap"
              >
                Subscribe
              </motion.button>
            </div>
          </div>
        </motion.div>

        {/* Bottom Bar */}
        <div className="border-t border-white/20 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <p className="text-gray-400 text-sm mb-4 md:mb-0">
              © 2025 Jansen's Bungalow Sinharaja Rainforest Retreat. Designed and Created by Rajantha
            </p>
            <div className="flex space-x-6">
              <a href="#" className="text-gray-400 hover:text-emerald-300 text-sm transition-colors">
                Privacy Policy
              </a>
              <a href="#" className="text-gray-400 hover:text-emerald-300 text-sm transition-colors">
                Terms of Service
              </a>
              <a href="#" className="text-gray-400 hover:text-emerald-300 text-sm transition-colors">
                Conservation Policy
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;